import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import UserRow from "@/components/admin/UserRow";
import { useAuth } from "@/context/AuthContext";

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const { user: me } = useAuth();

  const load = useCallback(() => {
    api.get("/admin/users", { params: { q } }).then(({ data }) => setUsers(data.users)).catch(() => {});
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (uid, status) => {
    const verb = status === "active" ? "reactivate" : status === "banned" ? "ban" : "suspend";
    const reason = (window.prompt(`Reason to ${verb} this account (required):`) || "").trim();
    if (reason.length < 3) return toast.error("A reason of at least 3 characters is required.");
    setBusy(uid + status);
    try {
      await api.patch(`/admin/users/${uid}/status`, { status, reason });
      toast.success(`User ${status}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const canDesignate = ["super_admin", "compliance_director"].includes(me?.role);

  const onMarkTest = async (u) => {
    const reason = (window.prompt(`Reason to classify ${u.email} as a synthetic test account (required):`) || "").trim();
    if (!reason) return toast.error("A reason is required.");
    if (!window.confirm(`Confirm: classify ${u.email} as automated_test? It will be excluded from real beta metrics.`)) return;
    setBusy(u.user_id + "marktest");
    try {
      await api.post("/admin/security/operators/designate", { user_id: u.user_id, origin: "automated_test", reason });
      toast.success(`${u.email} classified as synthetic-test`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || formatApiError(d) || "Classification failed");
    } finally { setBusy(""); }
  };

  const onPromote = async (uid, role) => {
    try { await api.patch(`/admin/users/${uid}/role`, { role }); toast.success(`Role: ${role}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="border border-slate-200 bg-white rounded-lg overflow-hidden">
      <div className="p-4 border-b border-slate-100 relative max-w-sm">
        <Search size={16} className="absolute left-7 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" data-testid="admin-user-search"
          className="w-full h-10 pl-10 pr-3 rounded-md border border-slate-300 focus:ring-2 focus:ring-[#2563EB] outline-none text-sm" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
              <th className="px-4 py-3 font-semibold">User</th>
              <th className="px-4 py-3 font-semibold">Business</th>
              <th className="px-4 py-3 font-semibold">Plan</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Trust</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <UserRow key={u.user_id} user={u} busy={busy} onStatus={onStatus} onPromote={onPromote} selfId={me?.user_id} onMarkTest={onMarkTest} canDesignate={canDesignate} />
            ))}
            {users.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No users found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
